/**
 * Taakgeneratie-regels — stuur bij hoe taken uit offerteregels worden voorgesteld.
 * Leesbaar overzicht van de regels + een chat om ze in gewone taal aan te passen.
 * Het model is instelbaar. Regels worden opgeslagen als setting `task_generation_rules`.
 */
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Stack, Paper } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { SettingField, SectionHeader, useSettings } from '../adminSettingsCommon';
import { AI_ENGINE_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

function asRules(val) {
  // Setting kan een object {regels:[...]} of (legacy) een string zijn.
  if (!val) return { regels: [] };
  if (typeof val === 'string') {
    try { const p = JSON.parse(val); return p && typeof p === 'object' ? p : { regels: [] }; }
    catch { return { regels: [] }; }
  }
  if (Array.isArray(val)) return { regels: val };
  return { regels: Array.isArray(val.regels) ? val.regels : [] };
}

function RegelsChat() {
  const { values, updateValue } = useSettings();
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const rules = asRules(values['task_generation_rules']);
  const regels = rules.regels || [];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isSaving = busy;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setErr(null);
    const nextMsgs = [...messages, { role: 'user', content: text }];
    setMessages(nextMsgs);
    setInput('');
    setBusy(true);
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/task-generation/rules-chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMsgs, rules }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'Aanpassen mislukt');
      const newRules = d.rules || rules;
      setMessages((m) => [...m, { role: 'assistant', content: d.reply || 'Aangepast.' }]);
      // Direct opslaan via de settings-PUT (niet via context-save: dat leest een
      // stale `values` uit de closure). updateValue reflecteert het meteen in de UI.
      const pr = await authFetch(`${AI_ENGINE_URL}/api/v1/admin/settings/task_generation_rules`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: newRules, updated_by: userEmail }),
      });
      if (!pr.ok) throw new Error('Opslaan van de regels mislukt');
      updateValue('task_generation_rules', newRules);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Box>
      {/* Leesbaar overzicht van de huidige regels */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569' }}>
            Regels die de generatie toepast
          </Typography>
          {isSaving && <CircularProgress size={14} />}
        </Box>
        {regels.length === 0 ? (
          <Typography variant="caption" color="text.secondary">Nog geen regels — gebruik de chat hieronder om ze op te bouwen.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {regels.map((r, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.75, fontSize: 13.5 }}>
                <span>•</span><span>{r}</span>
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Chat om de regels bij te sturen */}
      {messages.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {messages.map((m, i) => (
            <Box key={i} sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%', px: 1.5, py: 1, borderRadius: 1.5,
              bgcolor: m.role === 'user' ? '#e0e7ff' : '#f1f5f9',
              fontSize: 13.5, whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </Box>
          ))}
        </Stack>
      )}
      {err && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{err}</Typography>}
      <Stack direction="row" spacing={1}>
        <TextField fullWidth size="small" placeholder="Bijv. 'Voeg altijd een taak Projectafronding toe' of 'Bij animatie geen draaidag-taken'"
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button variant="contained" disabled={busy || !input.trim()} onClick={send}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
          Stuur bij
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Wijzigingen worden meteen opgeslagen en gelden voor het volgende voorstel. Het systeem leert daarnaast
        automatisch van correcties die PL's in het voorstel maken.
      </Typography>
    </Box>
  );
}

export default function TaakgeneratieRegels() {
  return (
    <Box>
      <SectionHeader
        title="Taakgeneratie uit offerteregels"
        subtitle="Hoe 'Slimme taken uit de offerte' een takenlijst voorstelt — model en regels."
      />
      <SettingField settingKey="task_generation_model" label="AI-model"
        helperText="Model dat het takenvoorstel maakt. Default gpt-5.5. Bump naar een sterker reasoning-model als de decompositie tegenvalt." />

      <Box sx={{ mt: 3 }}>
        <SectionHeader title="Regels (bijsturen via chat)" subtitle="Beschrijf in gewone taal hoe je het voorstel wilt; het systeem past de regels aan." />
      </Box>
      <RegelsChat />
    </Box>
  );
}
